import type { IPlatformData } from '@wix/editor-elements-integrations';
import { withCompController } from '@wix/editor-elements-integrations';
import {
  AnalyticsClicksGroups,
  tryReportAnalyticsClicksBi,
} from '@wix/editor-elements-common-utils';
import type {
  ILoginSocialBarControllerProps,
  ILoginSocialBarMapperProps,
  ILoginSocialBarProps,
  ILoginSocialBarStateValues,
} from '../LoginSocialBar.types';

export const useComponentProps = ({
  controllerUtils,
  stateValues,
  mapperProps,
}: IPlatformData<
  ILoginSocialBarMapperProps,
  ILoginSocialBarProps,
  ILoginSocialBarStateValues
>) => {
  const { updateProps } = controllerUtils;
  const {
    currentUrl,
    memberDetails,
    logout,
    reportBi,
    navigateTo,
    promptLogin,
  } = stateValues;
  const { compId, language, fullNameCompType, trackClicksAnalytics } =
    mapperProps;

  let membersData: {
    isLoggedIn?: boolean;
    userName?: string;
    avatarUri?: string;
  } = {
    isLoggedIn: false,
    userName: '',
    avatarUri: '',
  };

  if (
    memberDetails &&
    (!!memberDetails.nickname ||
      !!memberDetails.memberName ||
      !!memberDetails.loginEmail)
  ) {
    membersData.isLoggedIn = true;
    membersData.userName =
      memberDetails.nickname ||
      memberDetails.memberName ||
      memberDetails.loginEmail;
    membersData.avatarUri = memberDetails.imageUrl;
  } else if (
    mapperProps.isLoggedIn &&
    mapperProps.userName &&
    mapperProps.avatarUri
  ) {
    membersData = {};
  }

  const commonAnalyticsClickBiParams = {
    language,
    trackClicksAnalytics,
    element_id: compId,
    elementType: fullNameCompType,
    elementGroup: AnalyticsClicksGroups.Community,
  };

  const reportBiOnClick: ILoginSocialBarControllerProps['reportBiOnClick'] =
    label => {
      tryReportAnalyticsClicksBi(reportBi, {
        ...commonAnalyticsClickBiParams,
        elementTitle: label,
      });
    };

  const reportBiOnMenuItemClick: ILoginSocialBarControllerProps['reportBiOnMenuItemClick'] =
    item => {
      const { label, link, displayCount } = item;

      tryReportAnalyticsClicksBi(reportBi, {
        ...commonAnalyticsClickBiParams,
        value: link.href,
        elementTitle: label,
        details: { displayCount },
      });
    };

  const onMenuClose = () => {
    updateProps({
      isMenuOpen: false,
    });
  };

  const closeMenuOnBlur: React.FocusEventHandler = ({
    currentTarget,
    relatedTarget,
  }) => {
    if (!relatedTarget || !currentTarget.contains(relatedTarget as Node)) {
      onMenuClose();
    }
  };

  return {
    ...membersData,
    ...mapperProps,
    reportBiOnClick,
    reportBiOnMenuItemClick,
    onMenuOpen: () => {
      updateProps({
        isMenuOpen: true,
      });
    },
    onMenuClose,
    onBlur: closeMenuOnBlur,
    reportBi,
    currentPrimaryPageHref: currentUrl,
    navigateTo,
    onLogout: () => {
      void logout();
    },
    onLogin: () => {
      void promptLogin();
    },
  };
};

export default withCompController<
  ILoginSocialBarMapperProps,
  ILoginSocialBarControllerProps,
  ILoginSocialBarProps,
  ILoginSocialBarStateValues
>(useComponentProps);
